// StepReview — Phase 5 final step.
// Shows everything the operator filled in so they can sanity-check
// before the engine creates the tenant. ``onSubmit`` is fired with
// an empty payload — it only serves to mark the step as completed.
import React from 'react';
import { Button } from '../../../components/ui/button';
import { Eye, CheckCircle2 } from 'lucide-react';

function ReviewLine({ label, value }) {
  return (
    <div className="flex justify-between gap-3 py-1 border-b border-slate-100 last:border-0">
      <span className="text-slate-500 text-xs">{label}</span>
      <span className="text-slate-900 text-sm font-medium text-right break-all">
        {value === null || value === undefined || value === '' ? '—' : (typeof value === 'object' ? JSON.stringify(value) : String(value))}
      </span>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div data-testid={testid} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
      <h3 className="text-sm font-bold text-slate-800 mb-2">{title}</h3>
      <div>{children}</div>
    </div>
  );
}

export default function StepReview({ draft, onSubmit, busy }) {
  const s = draft?.steps || {};
  const identity  = s.identity || {};
  const structure = s.structure || {};
  const size      = s.size || {};
  const banks     = (s.banks || {}).accounts || [];
  const modules   = Object.entries((s.modules || {}).enabled || {}).filter(([, v]) => v).map(([k]) => k);
  const dataMode  = (s.data || {}).mode;
  const usersList = (s.users || {}).users || [];

  return (
    <div data-testid="step-review" className="space-y-5">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-indigo-50 text-indigo-700"><Eye size={18} /></div>
        <div>
          <h2 className="text-lg font-bold text-slate-900">Review &amp; confirm</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Take a look. Hit <b>Complete onboarding</b> to materialise the tenant — that triggers <code className="bg-slate-100 px-1 rounded">/api/onboarding/draft/<i>id</i>/complete</code>.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Section title="Identity" testid="review-identity">
          <ReviewLine label="Name (AR)"     value={identity.name_ar} />
          <ReviewLine label="Name (EN)"     value={identity.name_en} />
          <ReviewLine label="Trade name"    value={identity.trade_name} />
          <ReviewLine label="CR number"     value={identity.cr_number} />
          <ReviewLine label="VAT number"    value={identity.vat_number} />
          <ReviewLine label="Email"         value={identity.email} />
          <ReviewLine label="Phone"         value={identity.phone} />
          <ReviewLine label="City"          value={(identity.address || {}).city} />
        </Section>

        <Section title="Structure & size" testid="review-structure-size">
          <ReviewLine label="Legal structure" value={structure.legal_structure} />
          <ReviewLine label="Industry"        value={structure.industry} />
          <ReviewLine label="Employees"       value={size.employees} />
          <ReviewLine label="Annual revenue"  value={size.annual_revenue} />
          <ReviewLine label="Branches"        value={size.branches} />
        </Section>

        <Section title="Bank accounts" testid="review-banks">
          {banks.length === 0
            ? <div className="text-xs text-slate-400">No accounts set.</div>
            : banks.map((b, i) => (
                <ReviewLine
                  key={i}
                  label={`${b.bank_name || `Bank ${i + 1}`}${b.is_primary ? ' · primary' : ''}`}
                  value={b.iban || b.account_number}
                />
              ))}
        </Section>

        <Section title="Modules & data" testid="review-modules-data">
          <ReviewLine label="Modules enabled" value={modules.length ? modules.join(', ') : '—'} />
          <ReviewLine label="Initial data mode" value={dataMode} />
        </Section>

        <Section title="Users to provision" testid="review-users">
          {usersList.length === 0
            ? <div className="text-xs text-slate-400">No users defined — onboarding will create the company but nobody can log in until you add a user from Settings.</div>
            : usersList.map((u, i) => (
                <ReviewLine
                  key={i}
                  label={`${u.role || 'staff'} · ${u.full_name || u.email}`}
                  value={u.email}
                />
              ))}
        </Section>
      </div>

      {/* The wizard's bottom bar drives the actual complete call —
          this button is a soft "mark as reviewed" affordance only. */}
      <div className="flex justify-end">
        <Button
          variant="outline"
          onClick={() => onSubmit({ reviewed_at: new Date().toISOString() })}
          disabled={busy}
          data-testid="review-mark-btn"
        >
          <CheckCircle2 size={14} className="mr-1.5" /> Mark as reviewed
        </Button>
      </div>
    </div>
  );
}
